// 成員抽屜「<名字> 能做什麼」（SPEC #vms-web-787 IAM-07；VMS UX 評審 2026-10-13 P0-9）：
//   點任一成員（或全域搜尋找到人）→ 右側抽屜：組織角色、白話的組織權限與服務角色、依範圍列出所有有效權限與來源（同存取權清單篩這個人），
//   每列可撤銷（照該來源的權限與確認級別）；取代原本的「權限來源」對話框。網址 ?member=<user_id>，關掉就拿掉。
//   成員的管理動作（變更角色、組織權限、停用、移出）也在這裡，跟成員表格列尾〔⋯〕同一組。
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { isOrgAdmin, type MyOrg, type OrgMember } from '../../lib/planeApi';
import { Dialog } from '../../components/Dialog';
import { AccessList } from './AccessList';
import { memberName } from './MembersTab';
import { dateStamp } from '../../lib/timefmt';

/** 組織權限、服務角色、值班的白話句（「可以管帳單」…）。owner / org_admin = 組織的一切。 */
export function memberPowers(t: (k: string, o?: Record<string, unknown>) => string, m: Pick<OrgMember, 'role' | 'org_permissions' | 'service_roles' | 'duty_mode'>): string[] {
  if (m.role === 'owner' || m.role === 'org_admin') return [t('iam.can_all_org')];
  const out = m.org_permissions.map((p) => t('iam.can_' + p.replace('.', '_'), { defaultValue: '' })).filter(Boolean);
  for (const r of m.service_roles ?? []) out.push(t('iam.can_svc', { role: t(`svc.role_${r}`, { defaultValue: r }) }));
  if (m.duty_mode) out.push(t('iam.duty'));
  return out;
}

export function MemberDrawer({
  org,
  members,
  actions,
  maintenance,
}: {
  org: MyOrg;
  members: OrgMember[] | undefined;
  actions?: (m: OrgMember) => React.ReactNode;
  /** 停用 / 移出（第二輪 B2-06：只在抽屜最下方的「維護」區） */
  maintenance?: (m: OrgMember) => React.ReactNode;
}) {
  const { t } = useTranslation();
  const [sp, setSp] = useSearchParams();
  const id = sp.get('member');
  const m = id ? members?.find((x) => x.user_id === id) : undefined;
  const close = () => {
    const n = new URLSearchParams(sp);
    n.delete('member');
    setSp(n, { replace: true });
  };
  if (!id) return null;
  const powers = m ? memberPowers(t, m) : [];
  return (
    <Dialog open drawer testId="member-drawer" title={t('iam.drawer_title', { name: m ? memberName(m) : t('iam.left_member') })} onClose={close}>
      {m && (
        <dl className="drawer-facts">
          {m.email && m.display_name && (
            <>
              <dt>{t('auth.email')}</dt>
              <dd>{m.email}</dd>
            </>
          )}
          <dt>{t('iam.org_role')}</dt>
          <dd>
            {t(`people.org_role_${m.role}`, { defaultValue: t('iam.org_role_other') })} · {t(`people.status_${m.status}`, { defaultValue: t('common.state_unknown') })}
          </dd>
          <dt>{t('iam.powers')}</dt>
          <dd data-testid="member-powers">{powers.length ? powers.join(t('common.list_sep')) : t('iam.no_powers')}</dd>
          <dt>{t('people.joined')}</dt>
          <dd>{dateStamp(m.joined_at)}</dd>
        </dl>
      )}
      {m && actions && <div className="row">{actions(m)}</div>}
      <h3 className="section-title">{t('iam.drawer_access')}</h3>
      <p className="muted small">{t('iam.drawer_hint')}</p>
      {isOrgAdmin(org) ? <AccessList org={org} principal={id} compact /> : <p className="muted small">{t('iam.drawer_need_admin')}</p>}
      {m && maintenance && (() => {
        const body = maintenance(m);
        return body ? (
          <section className="maint-zone" data-testid="member-maintenance">
            <h4>{t('iam.maintenance')}</h4>
            <p className="muted small">{t('iam.maintenance_hint')}</p>
            <div className="row">{body}</div>
          </section>
        ) : null;
      })()}
    </Dialog>
  );
}
